import { createUser, fetchCompanyById } from "./apiService";
import { FidelityThemeConfig } from "./dto/fidelityCustomer.dto";
import { mapPlaceToAddress } from "./dto/user.dto";
import { FidelityCustomerFieldEnum } from "./enum/fidelityCustomerField.enum";
import { FALLBACK_LANGUAGE, getCurrentLanguage, TRANSLATIONS } from "./i18n/i18n";
import { countries } from "./models/country.model";
import {
  FidelityCardCustomerConfigField,
  fromDtoToFidelityCustomer,
} from "./models/fidelityCustomer.model";
import { showThankYou } from "./navigation";
import {
  getCompany,
  getCompanyId,
  getFormUser,
  getUser,
  setCompany,
  setFormUser,
  setTotalSteps,
  setUserProperty,
} from "./store";
import { showModal, updateProgressBar } from "./uiFunction";
import { validateInput } from "./validation";
import { fetchPreSignedUrl } from "./apiService";

// Global variables
let addressAutocomplete: google.maps.places.Autocomplete | null = null;

// to merge with fetchBrandingConfig
export async function fetchFormConfig(): Promise<void> {
  try {
    const companyData = await fetchCompanyById(
      getCompanyId(),
      getCurrentLanguage()
    );
    if (!companyData) throw new Error("Network response was not ok");

    setCompany(companyData);
    if (companyData.fidelityTheme && companyData.fidelityTheme.light) {
      applyTheme(companyData.fidelityTheme.light);
    }
    setFormUser(fromDtoToFidelityCustomer(companyData.fidelityConfig));
    // Apply the configuration to the form
    applyFormConfiguration();
  } catch (error) {
    console.error("Impossibile recuperare i dati dell'azienda:", error);
    showModal("Error loading form configuration");
  }
}

function applyTheme(theme: FidelityThemeConfig) {
  Object.entries(theme).forEach(([key, value]) => {
    document.documentElement.style.setProperty(`--${key}`, value);
  });
}

export async function submitForm(event: Event): Promise<any> {
  event.preventDefault();
  const submitButton = document.getElementById("submitButton") as HTMLButtonElement;
  submitButton.disabled = true;
  try {
    const privacyContainer = document.getElementById(
      "privacySettings"
    ) as HTMLElement;

    const inputs = privacyContainer.querySelectorAll("input");
    getUser().privacyPolicy = [];
    inputs.forEach((input) => {
      getUser().privacyPolicy.push({
        configFieldId: input.id,
        name: input.name,
        value: input.checked ? "true" : "false",
      });
    });
    
    const response = await createUser(
      getUser(),
      getCompanyId(),
      getCurrentLanguage()
    );

    if (!response) console.log("Network response was not ok");
    showThankYou();
    submitButton.disabled = false;
    //return response;
    return null;
  } catch (error: any) {
    if(error.statusCode === 409) {
      showModal("User already exists");
    } else {
      showModal("Error from server");
    }
    console.error("Error submitting form:", error);
    submitButton.disabled = false;
    throw error;
  }
}

async function showPrivacyModal(filePath: string) {
  try {
    const url = await fetchPreSignedUrl(filePath, getCompanyId());
    
    const pdfBlob = await downloadPDF(url);
    displayPDFInModal(pdfBlob);
    
  } catch (error) {
    console.error("Errore caricamento documento privacy:", error);
    showModal("Errore nel caricamento del documento privacy");
  }
}

async function downloadPDF(url: string): Promise<Blob> {
  const response = await fetch(url, {
    headers: {
      'Accept': 'application/pdf',
    }
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.blob();
}

function displayPDFInModal(blob: Blob) {
  const url = URL.createObjectURL(blob);
  const modal = document.getElementById("privacyFileModal");
  
  if (modal) {
    const iframe = modal.querySelector("iframe");
    if (iframe) {
      iframe.src = url;
      modal.classList.remove("hidden");

      const cleanup = () => {
        URL.revokeObjectURL(url);
        modal.removeEventListener('hidden', cleanup);
      };
      modal.addEventListener('hidden', cleanup);
    }
  }
}

export function applyFormConfiguration(): void {
  const customer = getFormUser();
  setTotalSteps(
    customer.additionalData && customer.additionalData.length > 0 ? 3 : 2
  );
  updateProgressBar(1);

  // Configure Step 1 Fields (all except additionalData)
  const step1 = document.getElementById("step1Fields") as HTMLElement;
  step1.innerHTML = ""; // Clear existing fields

  Object.entries(customer)
    .filter(
      ([fieldName, fieldValue]) =>
        fieldName !== "additionalData" &&
        fieldName !== "privacyPolicy" &&
        fieldValue !== FidelityCustomerFieldEnum.Hidden
    )
    .forEach(([fieldName, fieldValue]: [string, FidelityCustomerFieldEnum]) => {
          const configFieldId = customer.additionalData?.find(data => data.name === fieldName)?.id || fieldName;
          step1.appendChild(
            createFormField(
              fieldName,
              fieldValue === FidelityCustomerFieldEnum.Mandatory, // Required if type is Mandatory
              (TRANSLATIONS[getCurrentLanguage()].form as Record<string, string>)[
                fieldName
              ] || fieldName,
              configFieldId
            )
          );
        });
  
  if (customer.additionalData) {
    // Configure Step 2 Fields (only additionalData)
    const step2 = document.getElementById("step2Fields") as HTMLElement;
    step2.innerHTML = ""; // Clear existing fields

    if (
      customer.additionalData &&
      Array.isArray(customer.additionalData) &&
      customer.additionalData.length > 0
    ) {
      customer.additionalData.forEach(
        (data: FidelityCardCustomerConfigField) => {
          const label = data.text[getCurrentLanguage()] || data.text[FALLBACK_LANGUAGE]  || Object.values(data.text)[0];
          step2.appendChild(
            createFormField(
              label,
              data.mandatory,
              label,
              data.id
            )
          );
        }
      );
    }
  }

  // Configure Privacy Settings
  const privacyContainer = document.getElementById(
    "privacySettings"
  ) as HTMLElement;
  privacyContainer.innerHTML = "";

  if (customer.privacyPolicy) {
    customer.privacyPolicy.forEach((privacy) => {
      const div = document.createElement("div");
      const labelPrivacy = privacy.text[getCurrentLanguage()] || privacy.text[FALLBACK_LANGUAGE];
      div.className = "flex items-center";

      const input = document.createElement("input");
      input.type = "checkbox";
      input.id = privacy.id;
      input.name = labelPrivacy;
      input.className = "mr-2";
      if (privacy.mandatory) input.required = true;

      div.appendChild(input);

      const label = document.createElement("label");
      label.htmlFor = labelPrivacy;
      label.className = "select-none";
      label.innerHTML =
        labelPrivacy +
        (privacy.mandatory ? ' <span class="text-red-500">*</span>' : "");

      div.appendChild(label);

      privacyContainer.appendChild(div);
    });

    appendPrivacyPolicyLink();
  }

  if (document.getElementById("address")) {
    initGoogleAddressAutocomplete();
  }
}

async function appendPrivacyPolicyLink() {
  const privacyContainer = document.getElementById("privacySettings");
  const company = getCompany();
  const language = getCurrentLanguage();
  const file = company.fidelityPrivacyPolicyFiles.find(f => f.language === language) || company.fidelityPrivacyPolicyFiles[0];
  if (file && privacyContainer) {    
    const link = document.createElement("a");
    link.href = "#";
    link.className = "text-purple-600 underline hover:text-purple-800 block mt-2";
    link.innerText = (TRANSLATIONS[getCurrentLanguage()].form as Record<string, string>)["privacyPolicy"];
    link.onclick = async function(e) {
      e.preventDefault();
      await showPrivacyModal(file.filePath);
    };
    privacyContainer.appendChild(link);
  }
}

function createFormField(
  fieldName: string,
  required: boolean,
  label: string,
  configFieldId: string
): HTMLDivElement {
  const div = document.createElement("div");
  div.className = "w-full";

  // Create label element
  const labelElement = document.createElement("label");
  labelElement.className = "block text-sm font-medium text-gray-700 mb-1";
  labelElement.innerHTML = `
      <span data-translate="form.${fieldName}" id="span.${fieldName}">${label}</span>
      ${required ? '<span class="text-red-500 ml-1">*</span>' : ""}
    `;
  // Create input element
  const input = document.createElement("input");
  input.type =
    fieldName === "birthdate"
      ? "date"
      : fieldName === "email"
      ? "email"
      : fieldName === "phone"
      ? "tel"
      : "text";
  input.id = configFieldId;
  input.name = fieldName;
  input.className = "w-full p-2 border rounded";
  if (required) input.required = true;

  // Add real-time validation
  input.addEventListener("input", () => {
    validateInput(input, getCurrentLanguage());
  });

  // Validate on blur for better UX
  input.addEventListener("blur", () => {
    validateInput(input, getCurrentLanguage());
  });

  // Append elements to the container div
  div.appendChild(labelElement);

  if (fieldName === "phone") {
    const prefixDropdown = createPrefixDropdown();
    const inputContainer = document.createElement("div");
    inputContainer.className = "flex gap-2 justify-between";    
    input.className = "p-2 w-4/5 border rounded";

    inputContainer.appendChild(prefixDropdown);
    inputContainer.appendChild(input);
    div.appendChild(inputContainer);
  } else {
    div.appendChild(input);
  }

  return div;
}

// Initialize Google Address Autocomplete
function initGoogleAddressAutocomplete(): void {
  const addressInput = document.getElementById(
    "address"
  ) as HTMLInputElement | null;
  if (!addressInput) return;

  // Create autocomplete instance
  addressAutocomplete = new google.maps.places.Autocomplete(addressInput, {
    types: ["address"],
    fields: ["formatted_address", "address_components", "geometry"],
  });

  const observer = new MutationObserver(() => {
    const containers = document.getElementsByClassName("pac-container");
    Array.from(containers).forEach((container) => {
      (container as HTMLElement).style.zIndex = "9999";
      container.classList.add(
        "shadow-lg",
        "rounded-lg",
        "border",
        "border-gray-200",
        "bg-white",
        "mt-1"
      );
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  addressAutocomplete.addListener("place_changed", () => {
    const place = addressAutocomplete!.getPlace();

    if (!place.geometry) {
      showModal("Please select a valid address from the suggestions");
      return;
    }

    const mappedAddress = mapPlaceToAddress(place);

    setUserProperty("address", mappedAddress);
  });

  addressInput.addEventListener("keydown", (e) => {
    const containers = document.getElementsByClassName("pac-container");
    if (
      e.key === "Enter" &&
      containers.length > 0 &&
      containers[0].children.length > 0
    ) {
      e.preventDefault();
    }
  });
}

function createPrefixDropdown(): HTMLDivElement {
  const div = document.createElement("div");
  div.className = "relative flex items-center border rounded";

  const select = document.createElement("select");
  select.id = "prefix";
  select.name = "prefix";
  select.className = "w-full p-2 appearance-none bg-transparent pl-10";

  countries.forEach((country) => {
    const option = document.createElement("option");
    option.value = country.phone;
    option.innerText = `(${country.label.slice(0, 2)}) ${country.phone}`;
    select.appendChild(option);
  });

  const flagImage = document.createElement("img");
  flagImage.id = "prefix-flag";
  flagImage.className = "absolute left-2 w-5 h-3";
  flagImage.src = getImageUri(countries[0].code);

  select.addEventListener("change", (event) => {
    const selectedCountry = countries.find(
      (c) => c.phone === (event.target as HTMLSelectElement).value
    );
    if (selectedCountry) {
      flagImage.src = getImageUri(selectedCountry.code);
    }
  });

  div.appendChild(flagImage);
  div.appendChild(select);
  return div;
}

function getImageUri(code: string) {
  return `https://flagcdn.com/w20/${code.toLowerCase()}.png`;
}
